import store from '@/store/store';
import Axios, { AxiosRequestConfig } from 'axios';
import AxiosRetry from 'axios-retry';

const timeout = 15000;

export default class AxiosService {
	private readonly axiosRetry = AxiosRetry;

	protected axiosRetry2 = Axios.create({
		timeout,
		headers: {
			Accept: '*/*',
			'Accept-Language': 'da',
			environment: store.state.apiEnvironment, // default 'ci'
		},
	});
	private readonly axiosOnce = Axios.create({
		timeout,
		headers: {
			Accept: '*/*',
			'Accept-Language': 'da',
			environment: store.state.apiEnvironment,
		},
	});
	protected defaultUrl: string;
	private withCredentials = true;

	private readonly retryConfig = {
		retries: 2,
		retryDelay: (retryCount: number) => {
			return retryCount * 100; // time interval between retries
		},
		retryCondition: (error) => {
			if (!error?.response?.status) {
				return true;
			}
			return error?.response?.status > 240;
		},
		shouldResetTimeout: true,
	};

	constructor(url?: string) {
		this.defaultUrl = url;

		this.axiosRetry(this.axiosRetry2, this.retryConfig);

		this.axiosRetry2.interceptors.request.use(async (config) => {
			return await this.addHeaders(config);
		});

		this.axiosOnce.interceptors.request.use(async (config) => {
			return await this.addHeaders(config);
		});
	}

	public setWithCredentials(withCredentials: boolean) {
		this.withCredentials = withCredentials;
	}

	public async getQuery(url: string, params): Promise<any> {
		const url_ = url ? url : this.defaultUrl;
		try {
			return await this.axiosOnce.get(url_, this.buildGetParams(params));
		} catch (error) {
			if (error?.response) {
				return error.response;
			}
		}
	}

	public async get(url: string = this.defaultUrl): Promise<any> {
		try {
			return await this.axiosOnce.get(url, this.buildGetParams(undefined));
		} catch (error) {
			if (error?.response) {
				return error.response;
			}
		}
	}
	public getRetryDefaultUrl(params?: any): Promise<any> {
		return this.getRetry(undefined, params);
	}

	public async getRetry(url: string, params?: any): Promise<any> {
		let response;
		try {
			const url_ = url ? url : this.defaultUrl;
			response = await this.axiosRetry2.get(url_, this.buildGetParams(params));
			return response;
		} catch (error) {
			if (error?.response) {
				return error.response;
			}
		}
	}

	/** default url is used if Url param is undefined */
	public async post(url: string, params?: any): Promise<any> {
		try {
			const url_ = url ? url : this.defaultUrl;
			return await this.axiosOnce.post(url_, this.buildPostParams(params));
		} catch (error) {
			if (error?.response) {
				return error.response;
			}
		}
	}

	public async patch(url: string, params?: any): Promise<any> {
		try {
			const url_ = url ? url : this.defaultUrl;
			return await this.axiosOnce.patch(url_, this.buildPostParams(params));
		} catch (error) {
			if (error?.response) {
				return error.response;
			}
		}
	}

	public async delete(url: string, params?: any): Promise<any> {
		try {
			const url_ = url ? url : this.defaultUrl;
			return await this.axiosOnce.delete(url_, this.buildPostParams(params));
		} catch (error) {
			if (error?.response) {
				return error.response;
			}
		}
	}

	public async put(url: string, params?: any): Promise<any> {
		try {
			const url_ = url ? url : this.defaultUrl;
			return await this.axiosOnce.put(url_, this.buildPostParams(params));
		} catch (error) {
			if (error?.response) {
				return error.response;
			}
		}
	}

	private async addHeaders(config): Promise<AxiosRequestConfig> {
		if (config.method === 'patch' || config.method === 'put') {
			config.headers = {
				'Accept-Language': 'da',
				// 'X-Site-Name': store?.getters?.getThemeContext?.toLocaleUpperCase(),
				environment: store.state.apiEnvironment,
			};
			config.timeout = timeout * 4;
		} else {
			config.headers = {
				Accept: 'application/json',
				'Accept-Language': 'da',
				// 'X-Site-Name': store?.getters?.getThemeContext?.toLocaleUpperCase(),
				environment: store.state.apiEnvironment,
			};
		}
		return config;
	}

	private buildGetParams(params) {
		return Object.assign({}, { params }, { timeout, withCredentials: this.withCredentials });
	}

	private buildPostParams(params) {
		return Object.assign({}, params, { timeout, withCredentials: this.withCredentials });
	}
}
